import { ReferencedEntity } from '../../value-object/ReferencedEntity'
import {
  compareByStatus,
  isClosedStatus,
  WbsItemStatus,
} from '../WbsItemEntity'

export type ReferencedTicketListEntity = ReferencedEntity & {
  ticketType: string
}

// 選択肢として並べる際は完了済みの管理表と現役のものを見分ける必要があるため、
// 参照値にステータスを加えて扱う。
export type TicketListCandidateEntity = ReferencedTicketListEntity & {
  status: WbsItemStatus
}

// チケットタイプをまたいで並べるときの追加条件。チケットタイプの表示名はReduxが持つため、
// 純関数のままにできるよう名前解決を引数で受け取る。
export type TicketTypeSortOptions = {
  // 編集中のチケットと同じチケットタイプを先頭に置く
  currentTicketType: string
  ticketTypeNameOf: (ticketType: string) => string
}

// 選択肢の並び順。チケットタイプ（指定時のみ）→ 業務上の進行順 → 同ステータス内は名前順。
export const compareTicketListForDisplay = (
  a: { status: WbsItemStatus; name: string; ticketType?: string },
  b: { status: WbsItemStatus; name: string; ticketType?: string },
  typeSort?: TicketTypeSortOptions
): number => {
  if (typeSort && a.ticketType !== b.ticketType) {
    if (a.ticketType === typeSort.currentTicketType) return -1
    if (b.ticketType === typeSort.currentTicketType) return 1
    const nameOrder = typeSort
      .ticketTypeNameOf(a.ticketType ?? '')
      .localeCompare(typeSort.ticketTypeNameOf(b.ticketType ?? ''))
    // 表示名はユーザーが編集でき一意とは限らない。同名で0を返すと推移律が崩れ、
    // 無関係なタイプの候補まで同一グループが分裂するため、codeで決着をつける。
    if (nameOrder !== 0) return nameOrder
    return (a.ticketType ?? '').localeCompare(b.ticketType ?? '')
  }
  const statusOrder = compareByStatus(a.status, b.status)
  if (statusOrder !== 0) return statusOrder
  return a.name.localeCompare(b.name)
}

// 完了・破棄済みは既定で隠すが、選択中のものを消すと入力欄の値が候補から外れるため残す。
export const isTicketListVisible = (
  status: WbsItemStatus,
  uuid: string,
  showsClosed: boolean,
  selectedUuid: string | undefined
): boolean => showsClosed || !isClosedStatus(status) || uuid === selectedUuid

// 出し分けと並び順の判断に要る項目。呼び出し側が持つ型はAPIごとに異なるため、これに詰め替えて渡す。
export type TicketListDisplayKey = {
  uuid: string
  status: WbsItemStatus
  name: string
  ticketType?: string
}

export type TicketListDisplayOptions = {
  showsClosed: boolean
  selectedUuid: string | undefined
  // チケットタイプ順に並べる場合のみ指定する。統合チケット管理画面は単一タイプのため指定しない。
  typeSort?: TicketTypeSortOptions
}

// 選択肢として見せる管理表を、出し分けと並び順を揃えて取り出す。統合チケット管理画面と
// 単票画面で同じ規則にするため、両画面はこの関数を通す。
export const selectTicketListsForDisplay = <T>(
  ticketLists: T[],
  toDisplayKey: (ticketList: T) => TicketListDisplayKey,
  { showsClosed, selectedUuid, typeSort }: TicketListDisplayOptions
): T[] =>
  ticketLists
    .map(ticketList => ({ ticketList, key: toDisplayKey(ticketList) }))
    .filter(({ key }) =>
      isTicketListVisible(key.status, key.uuid, showsClosed, selectedUuid)
    )
    .sort((a, b) => compareTicketListForDisplay(a.key, b.key, typeSort))
    .map(({ ticketList }) => ticketList)
